// «Hent norske oversettelser»: typer og rene hjelpere for statusen fra
// /api/v1/admin/quran/norwegian/*.

export interface NorwegianMissingSurah {
  surahNumber: number;
  name: string;
  missingAyahs: number[];
}

export interface NorwegianReport {
  totalAyahs: number;
  missingAyahs: number;
  placeholderAyahs: number;
  surahs: NorwegianMissingSurah[];
  generatedAt?: string;
}

export interface NorwegianSurahSync {
  surahNumber: number;
  name: string;
  missingBefore: number;
  updatedAyahs: number;
  stillMissingAyahs: number[];
  message?: string | null;
}

export type NorwegianSyncState = "idle" | "running" | "done" | "failed";

export interface NorwegianSyncStatus {
  state: NorwegianSyncState;
  alreadyRunning?: boolean;
  startedAt?: string | null;
  finishedAt?: string | null;
  missingBefore: number;
  totalSurahs: number;
  processedSurahs: number;
  currentSurah?: number | null;
  updatedSurahs: number;
  updatedAyahs: number;
  missingAfter?: number | null;
  stillMissing: NorwegianMissingSurah[];
  failedSurahs: NorwegianSurahSync[];
  message?: string | null;
}

export const isRunning = (status: NorwegianSyncStatus | null): boolean =>
  status?.state === "running";

/** Fremdrift i prosent (0–100), basert på surah-er som er behandlet. */
export const progressPercent = (status: NorwegianSyncStatus): number => {
  if (status.state === "done") return 100;
  if (!status.totalSurahs) return 0;
  return Math.min(
    100,
    Math.round((status.processedSurahs / status.totalSurahs) * 100),
  );
};

/** «1–3, 5, 7–8» av versnummer. */
export const formatAyahRanges = (ayahs: number[]): string => {
  const sorted = Array.from(new Set(ayahs)).sort((a, b) => a - b);
  const parts: string[] = [];
  let start: number | null = null;
  let prev: number | null = null;
  const flush = () => {
    if (start === null || prev === null) return;
    parts.push(start === prev ? `${start}` : `${start}–${prev}`);
  };
  sorted.forEach((n) => {
    if (prev !== null && n === prev + 1) {
      prev = n;
      return;
    }
    flush();
    start = n;
    prev = n;
  });
  flush();
  return parts.join(", ");
};

/** «67 Al-Molk: vers 1–30» per surah. */
export const describeMissing = (surah: NorwegianMissingSurah): string =>
  `${surah.surahNumber} ${surah.name}: ${surah.missingAyahs.length} vers (${formatAyahRanges(surah.missingAyahs)})`;

/** Antall manglende vers per surah-nummer (til merkene i surah-listen). */
export const missingBySurah = (
  report: NorwegianReport | null,
): Record<number, number> =>
  Object.fromEntries(
    (report?.surahs ?? []).map((s) => [s.surahNumber, s.missingAyahs.length]),
  );

/** Kort oppsummering av en ferdig eller pågående synk. */
export const statusSummary = (status: NorwegianSyncStatus): string => {
  switch (status.state) {
    case "running":
      return status.totalSurahs
        ? `Henter norsk … ${status.processedSurahs} av ${status.totalSurahs} surah-er` +
            (status.currentSurah ? ` (nå: surah ${status.currentSurah})` : "") +
            `, ${status.updatedAyahs} vers oppdatert så langt.`
        : "Ser etter vers uten norsk oversettelse …";
    case "done":
      if (status.missingBefore === 0)
        return "Ingen vers manglet norsk oversettelse.";
      return (
        `Ferdig: ${status.updatedAyahs} av ${status.missingBefore} vers oppdatert ` +
        `i ${status.updatedSurahs} surah-er. ` +
        `${status.missingAfter ?? 0} vers mangler fortsatt` +
        (status.failedSurahs.length
          ? `, ${status.failedSurahs.length} surah-er feilet.`
          : ".")
      );
    case "failed":
      return status.message ?? "Synken stoppet.";
    default:
      return "";
  }
};

/** Er teksten en plassholder («kommer snart», «kommer», «un») eller tom? */
export const isMissingTranslation = (text?: string | null): boolean => {
  if (!text) return true;
  const plain = text
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;| /g, " ")
    .trim()
    .replace(/[.…!]+$/, "")
    .trim()
    .toLowerCase();
  return plain === "" || ["kommer snart", "kommer", "un"].includes(plain);
};
